/**
 * Modal behaviour shared by the dialogs and the navigation drawer.
 *
 * While a dialog (or the drawer on a narrow screen) is open, everything else
 * on the page is made `inert`: it cannot be clicked, focused or read by a
 * screen reader, so the open layer really is the only thing on screen. Tab
 * and Shift+Tab cycle inside it, Escape asks it to close, and focus goes back
 * to whatever opened it.
 */

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'summary', 'iframe',
  '[contenteditable="true"]', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Elements inside `container` a Tab press can reach, in document order. */
export function focusableIn(container) {
  return [...container.querySelectorAll(FOCUSABLE)].filter((el) => {
    if (el.closest('[inert]')) return false;
    if (el.getClientRects().length === 0) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  });
}

/**
 * Keep a Tab keypress inside `container`: from the last stop it wraps to the
 * first, from the first (or the container itself) Shift+Tab wraps to the last.
 *
 * @param {KeyboardEvent} event
 * @param {HTMLElement} container
 */
export function trapTab(event, container) {
  if (event.key !== 'Tab' || !container) return;
  const stops = focusableIn(container);
  const active = document.activeElement;
  if (stops.length === 0) {
    event.preventDefault();
    container.focus({ preventScroll: true });
    return;
  }
  const first = stops[0];
  const last = stops[stops.length - 1];
  const outside = !container.contains(active);
  if (event.shiftKey && (active === first || active === container || outside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || outside)) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Make everything outside `node` inert: every sibling of `node` and of each of
 * its ancestors up to <body>. Returns the function that undoes exactly what
 * this call did (an element that was already inert stays inert).
 *
 * @param {HTMLElement} node
 * @param {{ except?: Element[] }} [options] - siblings to leave interactive
 *   (the drawer's scrim, which closes it on click)
 */
export function inertOutside(node, { except = [] } = {}) {
  const changed = [];
  for (let child = node; child && child.parentElement; child = child.parentElement) {
    for (const sibling of child.parentElement.children) {
      if (sibling === child || except.includes(sibling) || sibling.inert) continue;
      if (['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName)) continue;
      sibling.inert = true;
      changed.push(sibling);
    }
    if (child.parentElement === document.body) break;
  }
  return () => { for (const el of changed) el.inert = false; };
}

/** Move focus back to `target`, or to the main content when it has gone. */
export function returnFocus(target) {
  if (target instanceof HTMLElement && target.isConnected && !target.closest('[inert]')) {
    target.focus({ preventScroll: true });
    if (document.activeElement === target) return;
  }
  document.getElementById('main-content')?.focus({ preventScroll: true });
}

/**
 * Svelte action for a modal panel (the element carrying role="dialog").
 *
 * @param {HTMLElement} node
 * @param {{ onClose?: () => void }} params - called on Escape; omit to ignore Escape
 */
export function modal(node, params = {}) {
  let onClose = params.onClose;
  const trigger = document.activeElement;
  if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
  // The backdrop is the dialog's own ancestor, so it stays clickable.
  const restoreInert = inertOutside(node);
  node.focus({ preventScroll: true });

  function onKeydown(event) {
    if (event.key === 'Tab') {
      trapTab(event, node);
      return;
    }
    if (event.key !== 'Escape' || !onClose) return;
    event.stopPropagation();
    onClose();
  }
  node.addEventListener('keydown', onKeydown);

  return {
    update(next = {}) { onClose = next.onClose; },
    destroy() {
      node.removeEventListener('keydown', onKeydown);
      restoreInert();
      returnFocus(trigger);
    },
  };
}
